import ExportButton from 'components/ExportButton/ExportButton';
import UncontrolledPopover from 'components/SafeUncontrolledPopover';
import PropTypes from 'prop-types';
import React, {
  CSSProperties,
  createRef,
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';
import { createCSVFileName } from 'utils/util/util';
import { ENGAGEMENT_SURVEY_OTHERS_EXPLANATION } from '../../../../consts/consts';
import ObjectsDropdown from '../../Dropdowns/ObjectsDropdown';
import RichTextViewer from '../../Inputs/RichTextViewer';
import HeatmapRow, { MAX_COL_WIDTH } from './HeatmapRow';
import StaticHeatmapRow from './StaticHeatmapRow';
import {
  GenerateCsvData,
  GetColorFunc,
  GetValueFunc,
  HeatmapState,
} from './types';

const HEADER_STYLE: CSSProperties = {
  display: 'inline-block',
};

const HEADER_TEXT_STYLE: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100px',
  transform: 'rotate(-45deg)',
  position: 'relative',
  top: '-20px',
};

const Heatmap: FC<Props> = (props) => {
  const { formatMessage } = useIntl();
  const [segment, setSegment] = useState(null);

  const [horizontalScrollPossible, setHorizontalScrollPossible] =
    useState(false);
  const scrollRef = useRef(null);

  const updateHorizontalScroll = useCallback(() => {
    const BUFFER = 15;

    // @ts-expect-error
    const scrollWidth = scrollRef.current?.scrollWidth;
    // @ts-expect-error
    const clientWidth = scrollRef.current?.clientWidth;
    // @ts-expect-error
    const scrollLeft = Math.ceil(scrollRef.current?.scrollLeft);

    setHorizontalScrollPossible(
      BUFFER + scrollLeft + clientWidth < scrollWidth
    );
  }, []);

  const propsFormatValue = props.formatValue;
  const propsGenerateCsvData = props.generateCsvData;

  useEffect(() => {
    updateHorizontalScroll();
  }, [scrollRef, segment, updateHorizontalScroll]);

  const allColumnHeaderText =
    // @ts-expect-error
    props?.data?.[0]?.configs?.columns_display_name?.all ??
    props?.allColumnHeaderText ??
    formatMessage({
      id: 'app.views.widgets.charts.heatmap.heatmap.all',
      defaultMessage: 'All',
    });

  const allColumnTooltipText =
    props.allColumnTooltipText ?? allColumnHeaderText;

  const segments = useMemo(() => {
    // Need to cycle through every question to get all
    // possible segments since some questions may get no
    // responses (so no segments would appear)
    let segmentList: string[] = [];

    props.data?.forEach((q) => {
      segmentList = [
        ...segmentList,
        // @ts-expect-error
        ...Object.keys(q?.responses ?? {}),
        // @ts-expect-error
        ...Object.keys(q?._all_by_breakdown ?? {}),
      ];
    });

    // Dedupe the segment list
    segmentList = [...new Set(segmentList)];
    segmentList.sort();

    // Formatting so it plays nice with the dropdown
    return segmentList?.map((s) => ({ id: s, name: s }));
  }, [props.data]);

  const onSegmentChangeHandler = useCallback(
    (e) => {
      const match = segments.find((s) => s.id === e.target.value);
      // @ts-expect-error
      setSegment(match);
    },
    [segments]
  );

  useEffect(() => {
    if (segment === null) {
      // @ts-expect-error
      setSegment(segments[0]);
    }
  }, [segment, segments]);

  const segmentValues = useMemo(() => {
    // Need to cycle through every question to get all
    // possible segment values since some questions may get no
    // responses (so no segments would appear). If no questions
    // contain a segment, we omit it
    let segmentValueList: string[] = [];
    const segmentValueSortKeyLookup: { [k: string]: string } = {};
    let segmentValueListSortable: string[] = [];

    if (segment) {
      props.data?.forEach((q) => {
        // @ts-expect-error
        const responses = q?.responses?.[segment.name] ?? {};
        const qSegmentValues = Object.keys(responses);
        Object.entries(responses)?.forEach(([k, v]) => {
          // @ts-expect-error
          segmentValueSortKeyLookup[k] = v.sort_key;
        });
        const qSortableSegmentValues = Object.entries(segmentValueSortKeyLookup)
          .map(([_, v]) => v)
          .filter((v) => v != null);
        segmentValueList.push(...qSegmentValues);
        segmentValueListSortable.push(...qSortableSegmentValues);
      });

      // Dedupe the segment list
      segmentValueList = [...new Set(segmentValueList)];
      if (segmentValueListSortable.length > 0) {
        segmentValueListSortable = [...new Set(segmentValueListSortable)];
        // Sort the segment values based on the sortable list
        segmentValueListSortable.sort();
        segmentValueList.sort((a, b) => {
          const aIndex = segmentValueListSortable.indexOf(
            segmentValueSortKeyLookup[a]
          );
          const bIndex = segmentValueListSortable.indexOf(
            segmentValueSortKeyLookup[b]
          );
          return aIndex - bIndex;
        });
      } else {
        segmentValueList.sort();
      }
    }

    return segmentValueList;
  }, [segment, props.data]);

  const createRows = useCallback(
    (
      columns: string[],
      data,
      firstColumnIsTitle = false,
      heatmapState: HeatmapState,
      isUnfrozen: boolean
    ) => {
      const groupedData = {};
      columns.forEach((c) => {
        let cTotal = 0;
        data.forEach((d) => {
          cTotal += d?.[c]?.responses?.count || 0;
        });

        groupedData[c] = cTotal;
      });

      const headers = [];
      if (props.isMultipleChoice) {
        // @ts-expect-error
        headers.push({ name: 'Respondents' });
        // @ts-expect-error
        headers.push({ name: 'Total' });
      }
      const headerRows = headers?.map((r, ri) => {
        const data = {
          configs: r,
          responses: groupedData,
        };

        return (
          <StaticHeatmapRow
            key={'static-' + ri}
            index={ri}
            columns={columns}
            data={data}
            firstColumnIsTitle={firstColumnIsTitle}
            formatValue={props.formatValue}
            total={!!ri}
            heatmapState={heatmapState}
          />
        );
      });

      const hasFactorHeaders = !!data.find((d) => d._isHeader);
      const bodyRows = data?.map((r, ri) => {
        return (
          <HeatmapRow
            key={ri}
            index={ri}
            columns={columns}
            data={r}
            firstColumnIsTitle={firstColumnIsTitle}
            colorScale={
              isUnfrozen
                ? (props.colorScaleUnfrozen ?? props.colorScale)
                : props.colorScale
            }
            formatValue={props.formatValue}
            formatPopover={props.formatPopover}
            popoverChart={props.popoverChart}
            isMultipleChoice={props.isMultipleChoice}
            unit={props.unit}
            hasFactorHeaders={hasFactorHeaders}
            heatmapState={heatmapState}
            isUnfrozen={isUnfrozen}
          />
        );
      });

      return [...headerRows, ...bodyRows];
    },
    [
      props.colorScale,
      props.colorScaleUnfrozen,
      props.formatValue,
      props.isMultipleChoice,
      props.formatPopover,
      props.popoverChart,
      props.unit,
    ]
  );

  const propsFrozenRowData = props.frozenRowData;

  const frozenRowData = useMemo(() => {
    // @ts-expect-error
    return propsFrozenRowData(segment?.name);
  }, [segment, propsFrozenRowData]);

  const propsUnfrozenRowData = props.unFrozenRowData;

  const unfrozenRowData = useMemo(() => {
    // @ts-expect-error
    return propsUnfrozenRowData(segment?.name);
  }, [segment, propsUnfrozenRowData]);

  const frozenRows = useMemo(() => {
    // These two columns are hardcoded for all heatmaps
    // since they are frozen for side scroll
    const headers = ['name', 'all'];
    return createRows(
      headers,
      frozenRowData,
      true,
      {
        segment: segment,
        headers: headers,
        frozenRowData: frozenRowData,
      },
      false
    );
  }, [segment, frozenRowData, createRows]);

  const unfrozenRows = useMemo(() => {
    const data = unfrozenRowData;
    return createRows(
      segmentValues,
      data,
      false,
      {
        segment: segment,
        frozenRowData: frozenRowData,
        unfrozenRowData: unfrozenRowData,
      },
      true
    );
  }, [segment, segmentValues, frozenRowData, unfrozenRowData, createRows]);

  const createCsvData = useCallback(() => {
    if (!propsGenerateCsvData) return [];
    return propsGenerateCsvData(
      segmentValues,
      frozenRowData,
      unfrozenRowData,
      propsFormatValue,
      allColumnHeaderText
    );
  }, [
    allColumnHeaderText,
    segmentValues,
    frozenRowData,
    unfrozenRowData,
    propsFormatValue,
    propsGenerateCsvData,
  ]);

  const exportButton = useMemo(
    () =>
      propsGenerateCsvData ? (
        <span
          className="ms-4"
          style={{ textTransform: 'initial', letterSpacing: 'initial' }}
        >
          <ExportButton filename={createCSVFileName()} data={createCsvData()} />
        </span>
      ) : null,
    [propsGenerateCsvData, createCsvData]
  );

  const frozenColumns = useMemo(() => {
    const columnHeaders = [
      {
        isElement: true,
        text: (
          <>
            {segments.length > 0 && (
              <>
                <span className="text-muted pe-2">
                  <FormattedMessage
                    id="app.views.widgets.charts.heatmap.heatmap.group_by"
                    defaultMessage="Group by"
                  />
                </span>
                <span>
                  <ObjectsDropdown
                    objects={segments}
                    // @ts-expect-error
                    value={segment?.id}
                    onChange={onSegmentChangeHandler}
                    size={'sm'}
                    className="d-inline"
                    renderItem={props.renderItem}
                  />
                </span>
                {exportButton}
              </>
            )}
          </>
        ),
      },
      { text: allColumnHeaderText, tooltip: allColumnTooltipText },
    ];

    return (
      <Row
        className="gx-0 mt-5 mb-4"
        style={{
          display: 'block',
          whiteSpace: 'nowrap',
        }}
      >
        {columnHeaders?.map((c, i) => {
          const ref = createRef<HTMLDivElement>();
          return (
            <Col
              key={i}
              className={i ? 'col-2 py-3' : 'col-10 '}
              style={HEADER_STYLE}
            >
              {c.isElement ? (
                <div>{c.text}</div>
              ) : (
                <Row>
                  <Col className="col-5 m-0 p-0"></Col>
                  <Col className="col-2 m-0 p-0">
                    <div ref={ref} style={HEADER_TEXT_STYLE}>
                      {c.text}
                    </div>
                  </Col>
                  <Col className="col-5 m-0 p-0"></Col>
                  <UncontrolledPopover
                    placement="top"
                    trigger="hover click"
                    target={ref}
                  >
                    {c.tooltip}
                  </UncontrolledPopover>
                </Row>
              )}
            </Col>
          );
        })}
      </Row>
    );
  }, [
    segments,
    // @ts-expect-error
    segment?.id,
    props.renderItem,
    allColumnHeaderText,
    allColumnTooltipText,
    onSegmentChangeHandler,
    exportButton,
  ]);

  const unfrozenColumns = useMemo(() => {
    return (
      <Row
        className="gx-0 mt-5 mb-4"
        style={{
          display: 'block',
          whiteSpace: 'nowrap',
        }}
      >
        {segmentValues?.map((c, i) => {
          const ref = createRef<HTMLDivElement>();
          // @ts-expect-error
          let rawValue = c?.name || c;

          // Handle "_others" separately, since it is a collection
          // of small employee segments
          const isOthers = rawValue === '_others';
          if (isOthers) {
            rawValue = 'others';
          }

          const formattedValue = rawValue;
          const id = 'header-' + i;
          return (
            <Col
              key={i}
              className={
                'py-3 col-2 ' +
                // @ts-expect-error
                (c.className ? c.className : '')
              }
              style={{ ...HEADER_STYLE, maxWidth: MAX_COL_WIDTH }}
            >
              <Row>
                <Col className="col-5 m-0 p-0"></Col>
                <Col className="col-2 m-0 p-0">
                  <div ref={ref} id={id} style={HEADER_TEXT_STYLE}>
                    {formattedValue}
                  </div>
                </Col>
                <Col className="col-5 m-0 p-0"></Col>
              </Row>

              {!isOthers && (
                <UncontrolledPopover
                  placement="top"
                  trigger="hover click"
                  target={ref}
                >
                  {rawValue}
                </UncontrolledPopover>
              )}
              {isOthers && (
                <UncontrolledPopover
                  placement="top"
                  trigger="hover click"
                  target={ref}
                >
                  {ENGAGEMENT_SURVEY_OTHERS_EXPLANATION(formatMessage)}
                </UncontrolledPopover>
              )}
            </Col>
          );
        })}
      </Row>
    );
  }, [segmentValues, formatMessage]);

  return (
    <div className={'m-4 fs-5'}>
      {props.additionalInputs ? (
        <Row>
          <Col className="heatmap-header">
            {props.additionalInputs.map((input, i) => (
              <Fragment key={i}>
                <span className={'text-muted pe-2' + (i ? ' ms-4' : '')}>
                  {input.label}
                </span>
                <span>{input.element}</span>
              </Fragment>
            ))}
            {!segments?.length && exportButton}
          </Col>
        </Row>
      ) : null}
      {props.showTitle && (
        <Row>
          <Col className="fw-bold text-dark col-12 fs-4">
            <RichTextViewer
              // @ts-expect-error
              model={props.data[0]?.displayName}
              expanded={false}
              afterContent={
                !segments?.length && !props.additionalInputs
                  ? exportButton
                  : null
              }
            />
          </Col>
        </Row>
      )}
      {!segments?.length &&
        !props.additionalInputs &&
        !props.showTitle &&
        exportButton && (
          <Row>
            <Col>{exportButton}</Col>
          </Row>
        )}
      <Row>
        <Col className="col-5 pe-0">
          <div className={'heatmap-header'}>{frozenColumns}</div>
          {frozenRows}
        </Col>
        <Col className="col-7 px-0">
          <div
            ref={scrollRef}
            style={{ overflowX: 'auto' }}
            onScroll={updateHorizontalScroll}
          >
            <Row className="mx-0">
              <Col className="px-0">
                <div className={'heatmap-header'}>{unfrozenColumns}</div>
                {unfrozenRows}
              </Col>
              {horizontalScrollPossible && (
                <Col
                  className={'col-1 h-100'}
                  style={{
                    width: '5%',
                    backgroundImage:
                      'linear-gradient(to right, transparent , white)',
                    right: '0%',
                    position: 'absolute',
                  }}
                >
                  <span></span>
                </Col>
              )}
            </Row>
          </div>
        </Col>
      </Row>
    </div>
  );
};

const Heatmap_propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  showTitle: PropTypes.bool,
  frozenRowData: PropTypes.func.isRequired,
  unFrozenRowData: PropTypes.func.isRequired,
  formatPopover: PropTypes.func,
  popoverChart: PropTypes.func,
  isMultipleChoice: PropTypes.bool,
  renderItem: PropTypes.func,
  unit: PropTypes.string,
};

export interface AdditionalInput {
  label: string | JSX.Element;
  element: JSX.Element;
}

type Props = PropTypes.InferProps<typeof Heatmap_propTypes> & {
  formatValue: GetValueFunc;
  additionalInputs?: AdditionalInput[];
  colorScale: GetColorFunc;
  colorScaleUnfrozen?: GetColorFunc;
  allColumnHeaderText?: string;
  allColumnTooltipText?: string;
  generateCsvData?: GenerateCsvData;
};

export default React.memo(Heatmap);
